import Slider from 'rc-slider/lib/Slider';
import { HTMLProps, useContext, useEffect, useState } from 'react';
import CurrencyInput from 'react-currency-input-field';
import { Trans, useTranslation } from 'react-i18next';
import { useGetPensionCalcData } from '../../../../api/pension-robot/useGetPensionCalcData';
import { DEFAULT_PENSION_AGE, FOR_LINK, MIN_AGE_CLIENT, riskProfiles } from '../../../../config/constants';
import { PensionContext } from '../../../../contexts/pension-robot/PensionContext';
import { reverseFormatNumber, toPrice } from '../../../../utils/parsers';
import { Button } from '../../../general/Button';
import { SelectInput } from '../../../general/form-inputs/SelectInput';
import { ModalScreenPension } from '../../../general/ModalScreen';
import { OverlayHeader } from '../../OverlayHeader';
import { AccountType } from '../../../../contexts/pension-robot/PensionContext';
import { getPerMonthFactor, getResultSavingsAccount, isMyBND } from '../../../../utils/functions';
import { useGetInvestmentForecast } from '../../../../api/pension-robot/useGetInvestmentForecast';
import { DateTime } from 'luxon';
import { LoadingStateWrapper } from '../../../general/LoadingStateWrapper';
import { usePostDialogueData } from '../../../../api/pension-robot/usePostPensionDialogueData';
import { RobotEnvironmentContext } from '../../../../contexts/general/RobotEnvironmentContext';
import { TooltipIndicator } from '../../../general/TooltipIndicator';
import { Tooltip } from '../../../general/Tooltip';
import { useGetPerMonthFactorValues } from '../../../../hooks/useGetPerMonthFactorValues';
import { Link } from '../../../general/Link';

export const AdditionalPension: React.FC<
    { setHiddenState: (hiddenState: boolean) => void } & HTMLProps<HTMLDivElement>
> = ({ setHiddenState }) => {
    const {
        actions,
        parameters: { supplementInfo, personalInfo },
    } = useContext(PensionContext);
    const { t } = useTranslation('pension');
    const { t: tg } = useTranslation('general');
    const [yearSpaceModalShown, setYearSpaceModalShown] = useState(false);
    const retirementForecastQuery = useGetPerMonthFactorValues(personalInfo.self?.age ?? MIN_AGE_CLIENT);
    const perMonthFactor = getPerMonthFactor(retirementForecastQuery.data);
    const [riskProfileId, setRiskProfileId] = useState(riskProfiles.neutral);
    const [typeOfAdditionalPensionAccount, setTypeOfAdditionalPensionAccount] = useState('saving' as AccountType);
    const [monthlyPaymentAmount, setMonthlyPaymentAmount] = useState(0);
    const { environment } = useContext(RobotEnvironmentContext);
    const usePostDialogueDataQuery = usePostDialogueData();
    const pensionCalcData = useGetPensionCalcData();
    const yearlyMarketRate = pensionCalcData.data?.marketRate ?? 0;
    const legalRetirementAge = pensionCalcData.data?.legalRetirementAge ?? DEFAULT_PENSION_AGE;

    const riskProfilePickList = [
        {
            label: t('general-very-defensive'),
            value: riskProfiles.veryDefensive,
        },
        {
            label: t('general-defensive'),
            value: riskProfiles.defensive,
        },
        {
            label: t('general-neutral'),
            value: riskProfiles.neutral,
        },
        {
            label: t('general-offensive'),
            value: riskProfiles.offensive,
        },
        {
            label: t('general-very-offensive'),
            value: riskProfiles.veryOffensive,
        },
    ];

    const investmentForecast = useGetInvestmentForecast(
        {
            riskProfileId: Number.parseInt(riskProfileId ?? '10') || 10,
            paymentSingleAmount: 0,
            paymentRecurringAmount: monthlyPaymentAmount,
            paymentRecurringInterval: 'Month',
            investmentEndDate: DateTime.now()
                .plus({
                    years: Math.max(
                        (personalInfo.self?.pensionAge ?? legalRetirementAge) - (personalInfo.self?.age ?? 0),
                        0,
                    ),
                })
                .toISODate(),
            paymentRecurringEndDate: DateTime.now()
                .plus({
                    years: Math.max(
                        (personalInfo.self?.pensionAge ?? legalRetirementAge) - (personalInfo.self?.age ?? 0),
                        0,
                    ),
                })
                .toISODate(),
            automaticRiskReduction: true,
            calculationMode: 'NewProduct',
        },
        false,
    );

    const calculateYearSpace = () => {
        const aowFranchise = pensionCalcData.data?.aowFranchise ?? 0;
        const maximumPensionFoundation = pensionCalcData.data?.maximumPensionFoundation ?? 0;
        const premiumPercentagePensionFoundation = pensionCalcData.data?.premiumPercentagePensionFoundation ?? 0;
        const pensionGrowthFactor = pensionCalcData.data?.pensionGrowthFactor ?? 0;

        const factorA = (supplementInfo?.yearSpaceParams?.factorA ?? 0) / 100;
        const FORIncrease = (supplementInfo?.yearSpaceParams?.FORIncrease ?? 0) / 100;
        const FORDecrease = (supplementInfo?.yearSpaceParams?.FORDecrease ?? 0) / 100;
        const grossYearlyIncome = (supplementInfo?.yearSpaceParams?.grossYearlyIncome ?? 0) / 100;

        const FOR = FORIncrease - FORDecrease;

        const res = Math.max(
            0,
            Math.ceil(
                premiumPercentagePensionFoundation *
                    (grossYearlyIncome < maximumPensionFoundation
                        ? grossYearlyIncome - aowFranchise
                        : maximumPensionFoundation - aowFranchise) -
                    pensionGrowthFactor * factorA -
                    FOR,
            ) * 100,
        );
        actions.setParameters({ supplementInfo: { yearSpace: res } });
        setMonthlyPaymentAmount(Math.floor(res / 12 / 100));
    };

    const investmentForcastMedianIsFetched = !!investmentForecast.data?.forecastExpirationValues?.median;
    const isInvestmentAndNotCalculatedYet =
        !investmentForcastMedianIsFetched && typeOfAdditionalPensionAccount === 'investment';

    const yearsUntilPension = (personalInfo.self?.pensionAge ?? legalRetirementAge) - (personalInfo.self?.age ?? 0);
    const computationResult =
        typeOfAdditionalPensionAccount === 'saving'
            ? getResultSavingsAccount(
                  0,
                  (monthlyPaymentAmount ?? 0) * 100,
                  'month',
                  yearsUntilPension,
                  yearlyMarketRate,
              )
            : investmentForecast.data?.forecastExpirationValues?.median
            ? investmentForecast.data?.forecastExpirationValues?.median * 100
            : 0;
    const computationResultPerMonth = computationResult / perMonthFactor;
    const yearOfPesnion = DateTime.now().plus({ year: yearsUntilPension }).year;

    const setAdditionalPension = () => {
        actions.setParameters({
            supplementInfo: {
                yearSpacePensionAccount: {
                    typeOfAdditionalPensionAccount: typeOfAdditionalPensionAccount,
                    riskProfileId: riskProfileId,
                    monthlyPaymentAmount: monthlyPaymentAmount ?? 0,
                    predictedAmount: computationResult,
                },
            },
        });
        isMyBND(environment) && usePostDialogueDataQuery.refetch();
    };

    const resetYearspace = () => {
        if (supplementInfo) {
            supplementInfo.yearSpacePensionAccount = {
                typeOfAdditionalPensionAccount: 'saving',
                riskProfileId: riskProfiles.neutral,
                monthlyPaymentAmount: undefined,
                predictedAmount: undefined,
            };

            if (supplementInfo.yearSpaceParams) {
                supplementInfo.yearSpaceParams = {
                    factorA: undefined,
                    FORIncrease: undefined,
                    FORDecrease: undefined,
                    grossYearlyIncome: undefined,
                };
            }
        }
        actions.setParameters({});
        isMyBND(environment) && usePostDialogueDataQuery.refetch();
    };

    useEffect(() => {
        calculateYearSpace();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (isInvestmentAndNotCalculatedYet && !investmentForecast.isLoading) {
            investmentForecast.refetch();
        }
    }, [supplementInfo?.yearSpace, typeOfAdditionalPensionAccount]); // eslint-disable-line react-hooks/exhaustive-deps

    const showYearspaceForm =
        supplementInfo?.yearSpaceParams?.factorA !== undefined &&
        supplementInfo?.yearSpaceParams?.factorA !== undefined;

    return (
        <div>
            {!showYearspaceForm ? (
                <div className="shadow-lg-all-sides p-6 mx-4 bg-white rounded-lg">
                    <div className="mb-4 pr-14">{t('pension-result-screen-additional-pension-subtitle1')}</div>
                    <div className="mb-6 pr-14">{t('pension-result-screen-additional-pension-subtitle2')}</div>
                    <div className="flex flex-row">
                        <Button
                            buttonType="primary"
                            className="font-medium"
                            onClick={() => setYearSpaceModalShown(true)}
                        >
                            {t('pension-result-screen-additional-pension-calculate-button')}
                        </Button>
                        <div className="flex-1" />
                    </div>
                </div>
            ) : (
                <div className="px-4">
                    <div className="rounded-lg bg-bnd-white-1 shadow-xl p-6 mt-4">
                        <div className="mb-8">
                            <Trans
                                t={t}
                                i18nKey={'pension-result-screen-additional-pension-explanation2'}
                                values={{
                                    maxYearSpace: toPrice(Math.floor((supplementInfo?.yearSpace ?? 0) / 100) * 100, {
                                        onlyFullEuros: true,
                                    }),
                                    maxYearSpaceMonth: toPrice(
                                        Math.floor((supplementInfo?.yearSpace ?? 0) / 12 / 100) * 100,
                                        { onlyFullEuros: true },
                                    ),
                                }}
                            ></Trans>
                        </div>
                        <span className="w-40 md:w-56 font-medium">
                            {t('pension-result-screen-additional-pension-type-label')}
                        </span>
                        <div className="flex flex-row items-center justify-start mb-6 mt-2">
                            <SelectInput
                                className="w-40 md:w-56"
                                onChange={(_value) => setTypeOfAdditionalPensionAccount(_value as AccountType)}
                                selected={typeOfAdditionalPensionAccount ?? 'saving'}
                                values={[
                                    {
                                        label: t('general-savings-account'),
                                        value: 'saving',
                                    },
                                    {
                                        label: t('general-investment-account'),
                                        value: 'investment',
                                    },
                                ]}
                            />
                        </div>
                        {typeOfAdditionalPensionAccount === 'investment' && (
                            <div>
                                <span className="w-40 md:w-56 font-medium">
                                    {t('pension-result-screen-additional-pension-risk-label')}
                                </span>
                                <div className="flex flex-row items-center justify-start mb-6 mt-2">
                                    <SelectInput
                                        className="w-40 md:w-56"
                                        onChange={(_value) => setRiskProfileId(_value)}
                                        selected={riskProfileId ?? '10'}
                                        values={riskProfilePickList}
                                    />
                                </div>
                            </div>
                        )}
                        <span className="font-medium">
                            {t('pension-result-screen-additional-pension-monthly-amount-label')}
                        </span>
                        <div className="flex flex-row items-center justify-start mb-6 mt-2">
                            <CurrencyInput
                                className={`w-28 md:w-28 py-2 px-4 mr-8 border border-gray-200 rounded-lg focus:outline-hidden font-medium`}
                                value={monthlyPaymentAmount === undefined ? undefined : monthlyPaymentAmount}
                                type="text"
                                allowDecimals={false}
                                intlConfig={{ currency: 'EUR', locale: 'nl-NL' }}
                                onValueChange={(_value) => {
                                    setMonthlyPaymentAmount(_value ? reverseFormatNumber(_value, 'nl-NL') : 0);
                                }}
                                placeholder={tg('euro-placeholder')}
                                prefix={tg('euro-placeholder')}
                            />
                            <div className={'lg:w-36 w-16'}>
                                <Slider
                                    max={Math.ceil((supplementInfo?.yearSpace ?? 0) / 100 / 12)}
                                    min={0}
                                    step={1}
                                    value={monthlyPaymentAmount ?? 0}
                                    onChange={(value) => {
                                        let _value = Array.isArray(value) ? value[0] : value;
                                        _value = _value < 0 ? 0 : _value;
                                        setMonthlyPaymentAmount(_value);
                                    }}
                                    onChangeComplete={(_value) => {
                                        if (isInvestmentAndNotCalculatedYet) investmentForecast.refetch();
                                    }}
                                />
                            </div>
                        </div>
                        <LoadingStateWrapper
                            isLoading={investmentForecast.isLoading}
                            isError={investmentForecast.isError}
                            isIdle={false}
                            isFetching={investmentForecast.isFetching}
                        >
                            <div className="mb-8">
                                <div>
                                    Verwachtte waarde in <b>{yearOfPesnion}</b>
                                </div>
                                <div>
                                    Totaal{' '}
                                    <b>
                                        {isInvestmentAndNotCalculatedYet
                                            ? '...'
                                            : toPrice(computationResult, { onlyFullEuros: true })}
                                    </b>
                                </div>
                                <div>
                                    Gemiddeld per maand{' '}
                                    <b>
                                        {isInvestmentAndNotCalculatedYet
                                            ? '...'
                                            : toPrice(computationResultPerMonth, { onlyFullEuros: true })}
                                    </b>
                                </div>
                            </div>
                            <div className="flex flex-col flex-wrap gap-2 sm:gap-4">
                                <Button
                                    buttonType="primary"
                                    disabled={(monthlyPaymentAmount ?? 0) < 0}
                                    onClick={() => {
                                        setAdditionalPension();
                                        setHiddenState(true);
                                    }}
                                >
                                    {t('pension-cocktail-fill-screen-results-save-or-invest-btn-add')}
                                </Button>
                                <Button
                                    buttonType="secondary"
                                    onClick={() => {
                                        resetYearspace();
                                    }}
                                >
                                    {t('general-cancel')}
                                </Button>
                                <Button
                                    buttonType={'secondary'}
                                    onClick={() => {
                                        setYearSpaceModalShown(true);
                                    }}
                                >
                                    {t('pension-result-screen-additional-pension-recalculate-button')}
                                </Button>
                                <div className="flex-1" />
                            </div>
                        </LoadingStateWrapper>
                    </div>
                </div>
            )}
            <YearSpaceModal
                isShown={yearSpaceModalShown}
                onClose={() => {
                    setYearSpaceModalShown(false);
                    calculateYearSpace();
                }}
            />
        </div>
    );
};

const YearSpaceModal: React.FC<{ isShown: boolean; onClose: () => void }> = ({ isShown, onClose }) => {
    const { t } = useTranslation('pension');
    const { t: tg } = useTranslation('general');
    const {
        parameters: { supplementInfo, personalInfo },
        actions,
    } = useContext(PensionContext);

    const previousYear = { previousYear: DateTime.local().year - 1 };
    const selfEmployed = personalInfo.self?.employmentStatus === 'self-employed';

    const [factorA, setFactorA] = useState<number | undefined>(undefined);
    const [grossYearlyIncome, setGrossYearlyIncome] = useState<number | undefined>(undefined);
    const [FORIncrease, setFORIncrease] = useState<number | undefined>(undefined);
    const [FORDecrease, setFORDecrease] = useState<number | undefined>(undefined);

    const applyYearSpaceParams = () => {
        if (supplementInfo) {
            supplementInfo.yearSpaceParams = {
                factorA,
                grossYearlyIncome,
                FORIncrease: selfEmployed ? FORIncrease : undefined,
                FORDecrease: selfEmployed ? FORDecrease : undefined,
            };
        }
        actions.setParameters({});
    };

    const onClick = () => {
        applyYearSpaceParams();
        onClose();
    };

    return (
        <ModalScreenPension isShown={isShown}>
            <div className="bg-white md:mx-auto md:rounded-xl p-4 sm:p-8">
                <OverlayHeader
                    title={t('pension-result-screen-additional-pension-yearspace-modal-title')}
                    onClose={onClose}
                />
                <div className="flex flex-col gap-4 mt-8">
                    <div className="flex flex-row flex-wrap gap-2 sm:gap-4 items-center justify-start">
                        <span className="w-40 md:w-80">
                            {t('pension-result-screen-additional-pension-yearspace-modal-income-label', previousYear)}
                        </span>
                        <CurrencyInput
                            type="text"
                            className={`w-40 md:w-56 py-2 px-4 border border-gray-200 rounded-lg focus:outline-hidden font-medium`}
                            fixedDecimalLength={0}
                            decimalsLimit={2}
                            intlConfig={{ currency: 'EUR', locale: 'nl-NL' }}
                            allowDecimals={false}
                            value={!!grossYearlyIncome ? grossYearlyIncome / 100 : undefined}
                            onValueChange={(value = '') => {
                                setGrossYearlyIncome(reverseFormatNumber(value, 'nl-NL') * 100);
                            }}
                            placeholder={tg('euro-placeholder')}
                            prefix={tg('euro-placeholder')}
                        />
                        <Tooltip
                            tooltipText={t('pension-result-screen-additional-pension-yearspace-modal-income-tooltip')}
                        >
                            <TooltipIndicator>?</TooltipIndicator>
                        </Tooltip>
                    </div>
                    <div className="flex flex-row flex-wrap gap-2 sm:gap-4 items-center justify-start">
                        <span className="w-40 md:w-80">
                            {t('pension-result-screen-additional-pension-yearspace-modal-factora-label', previousYear)}
                        </span>
                        <CurrencyInput
                            type="text"
                            className={`w-40 md:w-56 py-2 px-4 border border-gray-200 rounded-lg focus:outline-hidden font-medium`}
                            fixedDecimalLength={0}
                            decimalsLimit={2}
                            intlConfig={{ currency: 'EUR', locale: 'nl-NL' }}
                            allowDecimals={false}
                            value={!!factorA ? factorA / 100 : undefined}
                            onValueChange={(value = '') => {
                                setFactorA(reverseFormatNumber(value, 'nl-NL') * 100);
                            }}
                            placeholder={tg('euro-placeholder')}
                            prefix={tg('euro-placeholder')}
                        />
                        <Tooltip
                            tooltipText={t('pension-result-screen-additional-pension-yearspace-modal-factora-tooltip')}
                            place="bottom"
                        >
                            <TooltipIndicator>?</TooltipIndicator>
                        </Tooltip>
                    </div>
                    {selfEmployed && (
                        <div className="flex flex-row flex-wrap gap-2 sm:gap-4 items-center justify-start">
                            <span className="w-40 md:w-80">
                                {t(
                                    'pension-result-screen-additional-pension-yearspace-modal-forincrease-label',
                                    previousYear,
                                )}
                            </span>
                            <CurrencyInput
                                type="text"
                                className={`w-40 md:w-56 py-2 px-4 border border-gray-200 rounded-lg focus:outline-hidden font-medium`}
                                fixedDecimalLength={0}
                                decimalsLimit={2}
                                intlConfig={{ currency: 'EUR', locale: 'nl-NL' }}
                                allowDecimals={false}
                                value={!!FORIncrease ? FORIncrease / 100 : undefined}
                                onValueChange={(value = '') => {
                                    setFORIncrease(reverseFormatNumber(value, 'nl-NL') * 100);
                                }}
                                placeholder={tg('euro-placeholder')}
                                prefix={tg('euro-placeholder')}
                            />
                            <Tooltip
                                tooltipElement={
                                    <span>
                                        {t(
                                            'pension-result-screen-additional-pension-yearspace-modal-forincrease-tooltip-1',
                                        )}
                                        <Link href={FOR_LINK} target="_blank" rel="noreferrer">
                                            Oudedagsreserve (FOR)
                                        </Link>
                                        {t(
                                            'pension-result-screen-additional-pension-yearspace-modal-forincrease-tooltip-2',
                                        )}
                                    </span>
                                }
                            >
                                <TooltipIndicator>?</TooltipIndicator>
                            </Tooltip>
                        </div>
                    )}
                    {selfEmployed && (
                        <div className="flex flex-row flex-wrap gap-2 sm:gap-4 items-center justify-start">
                            <span className="w-40 md:w-80">
                                {t(
                                    'pension-result-screen-additional-pension-yearspace-modal-fordecrease-label',
                                    previousYear,
                                )}
                            </span>
                            <CurrencyInput
                                type="text"
                                className={`w-40 md:w-56 py-2 px-4 border border-gray-200 rounded-lg focus:outline-hidden font-medium`}
                                fixedDecimalLength={0}
                                decimalsLimit={2}
                                intlConfig={{ currency: 'EUR', locale: 'nl-NL' }}
                                allowDecimals={false}
                                value={!!FORDecrease ? FORDecrease / 100 : undefined}
                                onValueChange={(value = '') => {
                                    setFORDecrease(reverseFormatNumber(value, 'nl-NL') * 100);
                                }}
                                placeholder={tg('euro-placeholder')}
                                prefix={tg('euro-placeholder')}
                            />
                            <Tooltip
                                tooltipText={t(
                                    'pension-result-screen-additional-pension-yearspace-modal-fordecrease-tooltip',
                                )}
                            >
                                <TooltipIndicator>?</TooltipIndicator>
                            </Tooltip>
                        </div>
                    )}

                    <div className="flex gap-2 sm:gap-4 flex-row flex-wrap justify-start mt-8">
                        <Button
                            buttonType={'primary'}
                            onClick={() => {
                                onClick();
                            }}
                            className="w-30 sm:w-44 h-10"
                        >
                            {t('general-calculate')}
                        </Button>
                        <div style={{ flex: 6 }}></div>
                    </div>
                </div>
            </div>
        </ModalScreenPension>
    );
};
